import { LoadingOverlay } from "src/components/LoadingOverlay/LoadingOverlay.tsx";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { FormV2 } from "src/components/FormV2/FormV2.tsx";
import { composeRefs } from "@exonn/hooks";
import { RefreshButton } from "src/components/Form/RefreshButton/RefreshButton.tsx";
import { IconButtonV2 } from "src/components/IconButtonV2/IconButtonV2.tsx";
import { DeleteRecordConfirmation } from "src/admin/modals/DeleteRecordConfirmation/DeleteRecordConfirmation.tsx";
import { ButtonV2 } from "src/components/ButtonV2/ButtonV2.tsx";
import {
  isClassComponent,
  isFunctionComponent,
} from "src/components/Form/utils/isClassComponent.ts";
import { LanguageSelect } from "src/components/Form/LanguageSelect/LanguageSelect.tsx";
import { mapObjectToArray } from "@exonn/utils";
import { FormPanel } from "src/components/Form/FormPanel.tsx";
import React, { type CSSProperties, useState } from "react";
import {
  getResourceFormSavedScrollPositionKey,
  savedScrollPositions,
} from "src/system/savedScrollPositions.ts";

import { type FieldValues, useFormContext } from "react-hook-form";
import { QueryError } from "src/components/QueryError/QueryError.tsx";
import { useResourceFormContext as useFrameworkFormContext } from "src/components/Form/hooks/useResourceFormContext.ts";

import { useResourceFormQuery } from "src/components/Form/hooks/useResourceFormQuery.ts";
import { useResourceFormApi } from "src/components/Form/hooks/useResourceFormApi.ts";
import { useResourceFormTranslation } from "src/components/Form/hooks/useResourceFormTranslation.ts";
import { useRestoreScrollPosition } from "src/hooks/use-restore-scroll-position.ts";
import type { DefaultFormLayoutConfig } from "src/system/module.types.ts";
import { useIsFormOverlayLoading } from "src/components/Form/hooks/useIsFormOverlayLoading.ts";
import { FormAction } from "src/components/Form/Form.types.ts";

export const DefaultFormLayout = <
  FormValues extends FieldValues = FieldValues,
>({
  style,
  className,
  config: layout,
  onResetQueryError,
}: {
  style?: CSSProperties;
  className?: string;
  config: DefaultFormLayoutConfig<FormValues>;
  onResetQueryError?: () => void;
}) => {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const { t } = useResourceFormTranslation();

  const { refetch, error, isFetching } = useResourceFormQuery();

  const { resource, action, id, availableLocales, lang, onSubmit } =
    useFrameworkFormContext();

  const formApi = useResourceFormApi();

  const form = useFormContext<FormValues>();

  const locale = lang;
  const {
    hideRefreshButton,
    hideSaveButton,
    saveButtonIcon,
    showDeleteButton,
    additionalButtons,
    hideLanguages,
  } = layout.toolbar || {};

  const leftPanels = layout?.leftPanels || {};
  const rightPanels = layout?.rightPanels || {};
  const bottomPanels = layout?.bottomPanels || {};
  const rows = layout?.rows || [];

  const overlayLoading = useIsFormOverlayLoading();

  const scrollPositionKey = getResourceFormSavedScrollPositionKey({
    resource,
    action,
    // @ts-ignore
    id: id,
  });
  const { containerRef: connectScrollContainerRef } = useRestoreScrollPosition({
    onSavePosition: ({ top }) => {
      savedScrollPositions[scrollPositionKey] = top;
    },
    getSavedPosition: () => ({
      top: savedScrollPositions[scrollPositionKey],
    }),
  });
  if (error && !isFetching) {
    return (
      <QueryError
        onReset={() => {
          onResetQueryError?.();
          refetch();
        }}
        resource={resource}
        error={error}
      />
    );
  }

  return (
    <FormV2.Root
      style={style}
      className={className}
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <LoadingOverlay.Root isLoading={overlayLoading}>
        <LoadingOverlay.Spinner />
        <LoadingOverlay.Title>
          {t("advices.pleaseWait", {
            ns: TranslationNamespaceEnum.Common,
          })}
        </LoadingOverlay.Title>
      </LoadingOverlay.Root>
      <FormV2.Inner ref={composeRefs(connectScrollContainerRef)}>
        <FormV2.Toolbar>
          {!hideRefreshButton && <RefreshButton />}
          {showDeleteButton && action === FormAction.Edit && (
            <IconButtonV2
              variant={"outlined"}
              size={"sm"}
              onClick={() => {
                setShowDeleteConfirmation(true);
              }}
              skin={"danger"}
            >
              <i className={"icon-15-trash"} />
            </IconButtonV2>
          )}
          {showDeleteConfirmation && (
            <DeleteRecordConfirmation
              onClose={() => {
                setShowDeleteConfirmation(false);
              }}
              onCancel={() => {
                setShowDeleteConfirmation(false);
              }}
              onConfirm={() => {
                setShowDeleteConfirmation(false);
                formApi.delete();
              }}
            />
          )}
          {!hideSaveButton && (
            <ButtonV2
              size="sm"
              variant="filled"
              skin="success"
              startAdornment={
                saveButtonIcon ?? <span className={"icon-15-save"} />
              }
              type="submit"
              children={t("toolbar.save", {
                defaultValue: t("actions.save", {
                  ns: TranslationNamespaceEnum.Common,
                }),
              })}
            />
          )}
          {(additionalButtons || []).map((Button, index) =>
            isClassComponent(Button) || isFunctionComponent(Button) ? (
              <Button key={index} />
            ) : (
              Button
            ),
          )}

          {!!availableLocales.length && !hideLanguages && (
            <div css={{ marginLeft: "auto" }}>
              <LanguageSelect
                disabled={false}
                value={locale}
                availableLocales={availableLocales}
                onChange={formApi.changeLanguage}
              />
            </div>
          )}
        </FormV2.Toolbar>
        <FormV2.Grid>
          <FormV2.Column>
            {mapObjectToArray(leftPanels, (value, key) => {
              return (
                <FormPanel<FormValues> key={key} panelKey={key} panel={value} />
              );
            })}
          </FormV2.Column>
          <FormV2.Column>
            {mapObjectToArray(rightPanels, (value, key) => {
              return (
                <FormPanel<FormValues> key={key} panelKey={key} panel={value} />
              );
            })}
          </FormV2.Column>
        </FormV2.Grid>

        {rows.length > 0 &&
          rows.map((row, index) => (
            <FormV2.Grid key={index}>
              <FormV2.Column>
                {row.left && (
                  <FormPanel<FormValues>
                    panelKey={row.left.key}
                    panel={row.left.config}
                  />
                )}
              </FormV2.Column>
              <FormV2.Column>
                {row.right && (
                  <FormPanel<FormValues>
                    panelKey={row.right.key}
                    panel={row.right.config}
                  />
                )}
              </FormV2.Column>
            </FormV2.Grid>
          ))}

        {Object.keys(bottomPanels).length > 0 && (
          <FormV2.Footer>
            {mapObjectToArray(bottomPanels, (value, key) => {
              return (
                <div key={key}>
                  <value.Component />
                </div>
              );
            })}
          </FormV2.Footer>
        )}
      </FormV2.Inner>
    </FormV2.Root>
  );
};
